import { Box, Button, Link, Stack, Typography } from "@mui/material";
import CssBaseline from "@mui/material/CssBaseline";
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { Outlet } from "react-router-dom";
import "./App.css";
import { DarkModeSwitch } from "./DarkModeSwitch.tsx";
import { ExpandableText } from "./ExpandableText.tsx";
import { useNavItemProvider } from "./NavItemProvider.tsx";
import { NavPopup } from "./NavPopup.tsx";
import { useDetectWidthChange } from "./useDetectWidthChange.tsx";
import { useNavTileScale } from "./useNavTileScale.tsx";

const DEFAULT_HEIGHT = 200;
const DEFAULT_WIDTH = 300;

const getRandomCoordinate = (max: number, buffer = 0) => {
  return Math.floor(Math.random() * (max - buffer * 2) + buffer);
};

const isOverlapping = (
  newSquare: { top: number; left: number; width: number; height: number },
  squares: { top: number; left: number; width: number; height: number }[],
) => {
  const newWidth = newSquare.width;
  const newHeight = newSquare.height;
  return squares.some((square) => {
    const squareWidth = square.width;
    const squareHeight = square.height;

    //return true if newSquare overlaps with any existing square
    return (
      newSquare.top < square.top + squareHeight &&
      newSquare.top + newHeight > square.top &&
      newSquare.left < square.left + squareWidth &&
      newSquare.left + newWidth > square.left
    );
  });
};

export type NavSquare = {
  left: number;
  top: number;
  title: string;
  path: string;
};

function App() {
  const mainTextRef = useRef<HTMLDivElement>();
  const [squares, setSquares] = useState<NavSquare[]>([]);
  const { setFocusedNavItem } = useNavItemProvider();

  const resetSquares = () => setSquares([]);
  useDetectWidthChange(resetSquares);

  const scale = useNavTileScale();
  const addSquare = (args: { title: string; path }) => {
    const { title, path } = args;

    const width = DEFAULT_WIDTH * scale;
    const height = DEFAULT_HEIGHT * scale;

    const textRect = mainTextRef.current?.getBoundingClientRect();
    const textSquare = {
      top: textRect?.top ?? 0,
      left: textRect?.left ?? 0,
      width: textRect?.width ?? 0,
      height: textRect?.height ?? 0,
    };

    //try to add a new square. if it overlaps with the text, try again
    let attempts = 0;
    let newSquare;

    while (!newSquare && attempts < 1000) {
      const left = getRandomCoordinate(window.innerWidth - width);
      const top = getRandomCoordinate(window.innerHeight - height);

      newSquare = { top, left, title, path, width, height };

      if (isOverlapping(newSquare, [textSquare])) {
        newSquare = undefined;
      }

      attempts++;
    }

    if (newSquare) {
      //focus the new square
      setFocusedNavItem(path);
      setSquares((prevSquares) => [...prevSquares, newSquare]);
    } else {
      console.error("Could not add square");
    }
  };

  const projectsRef = useRef<{ click: () => void; clickWork: () => void }>(
    null,
  );
  const musicRef = useRef<{ click: () => void }>(null);
  const laRecsRef = useRef<{ click: () => void }>(null);
  const contactRef = useRef<{ click: () => void }>(null);

  const [showAnimateAll, setShowAnimateAll] = useState(true);

  useEffect(() => {
    if (squares.length > 0 && showAnimateAll) {
      setShowAnimateAll(false);
    }
  }, [squares.length, showAnimateAll]);

  const handleAnimateAll = () => {
    setShowAnimateAll(false);
    if (!squares.some((square) => square.path === "projects")) {
      projectsRef?.current?.click();
    }
    if (!squares.some((square) => square.path === "work")) {
      setTimeout(() => {
        projectsRef?.current?.clickWork();
      }, 0);
    }
    if (!squares.some((square) => square.path === "music")) {
      setTimeout(() => {
        musicRef?.current?.click();
      }, 1000);
    }
    if (!squares.some((square) => square.path === "la-recs")) {
      setTimeout(() => {
        laRecsRef?.current?.click();
      }, 3000);
    }
    if (!squares.some((square) => square.path === "contact")) {
      setTimeout(() => {
        contactRef?.current?.click();
      }, 3000);
    }
  };

  return (
    <>
      <CssBaseline />
      <div className="App">
        <Outlet />
        <Box
          sx={{
            height: "100vh",
            width: "100vw",
            position: "absolute",
            alignItems: "center",
            justifyContent: "center",
            display: "flex",
            backgroundColor: "background.default",
            transition: "all 0.6s",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "row-reverse",
              justifyContent: "space-between",
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              padding: 2,
            }}
          >
            <DarkModeSwitch />
          </Box>
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 50,
              pointerEvents: "none",
              overflow: "hidden",
            }}
          >
            {squares.map((square, index) => {
              return <NavPopup {...square} key={index} />;
            })}
          </Box>

          <Box
            sx={(theme) => ({
              maxWidth: "50vw",
              [theme.breakpoints.down("sm")]: {
                maxWidth: "80vw",
              },
            })}
          >
            <Box
              ref={mainTextRef}
              sx={(theme) => ({
                lineHeight: 3,
                fontSize: "1rem",
                [theme.breakpoints.down("md")]: {
                  fontSize: "0.8rem",
                },
                [theme.breakpoints.down("sm")]: {
                  lineHeight: 2.5,
                },
                animationDelay: "2s",
                animation: "fadeIn 2s",
                "@keyframes fadeIn": {
                  "0%": { opacity: 0 },
                  "100%": { opacity: 1 },
                },
              })}
            >
              Trevor Holt is a <SWText ref={projectsRef} addEntry={addSquare} />{" "}
              and <MusicianText addEntry={addSquare} ref={musicRef} /> based in{" "}
              <LAText ref={laRecsRef} addEntry={addSquare} />. You can reach him
              by <EmailText ref={contactRef} addEntry={addSquare} />.
            </Box>
          </Box>

          <Box
            sx={{
              position: "absolute",
              bottom: 0,
              right: 0,
              padding: 2,
              display: "flex",
              justifyContent: "center",
              width: "100%",
              color: "text.secondary",
            }}
          >
            <Stack spacing={2}>
              {showAnimateAll && (
                <Button onClick={handleAnimateAll}>Expand All</Button>
              )}
              <Typography variant="caption">© 2026 Trevor Holt</Typography>
            </Stack>
          </Box>
        </Box>
      </div>
    </>
  );
}

type SpecialTextProps = {
  addEntry: (entry: { title: string; path: string }) => void;
};

const SWText = forwardRef<
  {
    clickWork: () => void;
    click: () => void;
  },
  SpecialTextProps
>((props, forwardedRef) => {
  const workRef = useRef<{ click: () => void }>(null);
  const projectsRef = useRef<{ click: () => void }>(null);

  useImperativeHandle(
    forwardedRef,
    () => ({
      clickWork: () => {
        setTimeout(() => {
          workRef?.current?.click();
        }, 6 * 350);
      },
      click: () => {
        projectsRef?.current?.click();
      },
    }),
    [workRef],
  );
  return (
    <ExpandableText
      ref={projectsRef}
      associatedNavTitle="projects"
      preText={["full", "stack"]}
      mainText="software engineer"
      onClick={() => props.addEntry({ title: "Projects", path: "projects" })}
      postText={[
        "with",
        "extensive",
        <ExpandableText
          ref={workRef}
          associatedNavTitle="work"
          animate={false}
          mainText="startup experience"
          onClick={() => props.addEntry({ title: "Work", path: "work" })}
          postText={[
            "(in",
            "industries",
            "like",
            <>
              <Link href="https://www.headway.co/">mental health</Link>,
            </>,
            <>
              <Link href="https://www.zapshot.me/">social media</Link>,
            </>,
            <>
              <Link href="https://www.instagram.com/rdyapp/">
                independent coffee
              </Link>
              ,
            </>,
            "and",
            <>
              <Link href="https://www.zapshot.me/">social media</Link>
              {")"}
            </>,
          ]}
        />,
      ]}
    />
  );
});

const MusicianText = forwardRef<{ click: () => void }, SpecialTextProps>(
  (props: SpecialTextProps, ref) => {
    return (
      <ExpandableText
        ref={ref}
        associatedNavTitle="music"
        mainText="musician"
        postText={"(with a focus in rudimental percussion)".split(" ")}
        onClick={() => props.addEntry({ title: "Music", path: "music" })}
      />
    );
  },
);

const LAText = forwardRef<{ click: () => {} }, SpecialTextProps>(
  (props, ref) => {
    return (
      <ExpandableText
        ref={ref}
        associatedNavTitle="la-recs"
        mainText="Los Angeles"
        postText={"(most of the time)".split(" ")}
        onClick={() => props.addEntry({ title: "LA Recs", path: "la-recs" })}
      />
    );
  },
);

const EmailText = forwardRef<{ click: () => void }, SpecialTextProps>(
  (props: SpecialTextProps, ref) => {
    return (
      <ExpandableText
        ref={ref}
        associatedNavTitle="contact"
        mainText="email"
        postText={[
          ..."at trevholt1220@gmail.com, or connect on".split(" "),
          <Link href="https://www.linkedin.com/in/tholt1220/">LinkedIn</Link>,
        ]}
        onClick={() => props.addEntry({ title: "Contact", path: "contact" })}
      />
    );
  },
);

export default App;
